import { Dismiss24Regular, Open24Regular } from "@fluentui/react-icons";
import DOMPurify from "dompurify";
import { useState, useEffect } from "react";
import { ActionButton } from "../ActionButton";
import styles from "./SourceModal.module.css";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    content: string;
    filepath?: string;
}

interface Documento {
    id: string;
    title: string;
    year: string;
    subdireccion: string;
    categoria: string;
    filepaths: string[];
    sharepoint: string;
}

import { formatStudyTitle } from "../../utils/formatters";

export const SourceModal = ({ isOpen, onClose, title, content, filepath }: Props) => {
    const cleanedContent = content.replace(/:unselected:/gi, '').trim().replace(/^[\.\s]+/, "");
    const sanitizedContent = DOMPurify.sanitize(cleanedContent);
    const [sharepointUrl, setSharepointUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!title && !filepath) return;

        // Cargar documentos.json para encontrar el URL de SharePoint
        fetch('/documentos.json')
            .then(response => response.json())
            .then((documentos: Documento[]) => {
                // Normalización para comparaciones robustas (elimina todo lo no alfanumérico)
                const normalize = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9áéíóúñ]/g, '');
                
                const searchTarget = normalize(filepath || title);
                
                // Buscar el estudio que contenga este documento
                const documento = documentos.find(doc =>
                    doc.filepaths.some(fp => {
                        const normFp = normalize(fp);
                        return searchTarget === normFp || searchTarget.includes(normFp) || normFp.includes(searchTarget);
                    })
                );
                
                if (documento && documento.sharepoint) {
                    setSharepointUrl(documento.sharepoint);
                } else {
                    setSharepointUrl(null);
                }
            })
            .catch(error => {
                console.error('Error cargando documentos.json:', error);
                setSharepointUrl(null);
            });
    }, [title, filepath]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleOpenSharePoint = () => {
        if (sharepointUrl) {
            window.open(sharepointUrl, '_blank');
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerButtons}>
                        {sharepointUrl && (
                            <ActionButton
                                variant="primary"
                                onClick={handleOpenSharePoint}
                                aria-label="Ver documentos asociados"
                                title="Ver documentos asociados en SharePoint"
                                className={styles.sharepointButton}
                                icon={<Open24Regular />}
                            >
                                Ver documentos asociados
                            </ActionButton>
                        )}
                        <button
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Cerrar"
                        >
                            <Dismiss24Regular />
                        </button>
                    </div>
                    <h2 className={styles.modalTitle}>{formatStudyTitle(title)}</h2>
                </div>
                <div
                    className={styles.modalBody}
                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
            </div>
        </div>
    );
};
